import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { StaffPinCard } from "@/components/booking/UserSwitch";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, ArrowLeft, Save, CalendarClock, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import { COUNTRY_PRESETS, employmentCost } from "@/lib/business";
import { STAFF_COLORS } from "@/lib/palette";

const COLORS = STAFF_COLORS;
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEFAULT_WH = {
  mon: { open: true, start: "09:00", end: "17:00" }, tue: { open: true, start: "09:00", end: "17:00" },
  wed: { open: true, start: "09:00", end: "17:00" }, thu: { open: true, start: "09:00", end: "17:00" },
  fri: { open: true, start: "09:00", end: "17:00" }, sat: { open: false, start: "09:00", end: "13:00" },
  sun: { open: false, start: "09:00", end: "13:00" },
};
const empty = { name: "", email: "", phone: "", password: "", role: "staff", color: "#3E4552", can_be_booked: true, service_ids: [], base_hourly_rate: 32, employment_type: "full_time", hours_per_week: 38, super_pct: 12, annual_leave_weeks: 4, leave_loading_pct: 17.5, personal_leave_days: 10, public_holiday_days: 11, workers_comp_pct: 2, casual_loading_pct: 25, kpi_multiplier: "", profit_share_enabled: false, profit_share_pct: 20, profit_share_threshold: 5000, working_hours: null, permissions: {} };

const PERMS = [
  ["view_dashboard", "Dashboard & KPIs", "See the business dashboard and performance stats"],
  ["view_reports", "Sales & reports", "Open Sales, no-show, future-value & new-client reports"],
  ["give_discounts", "Give discounts", "Apply price adjustments & discounts at checkout"],
  ["edit_clients", "Edit client files", "Create and edit client records"],
  ["manage_inventory", "Manage inventory", "Add and edit retail stock"],
];
const ROLE_DEFAULTS = {
  owner: { view_dashboard: true, view_reports: true, give_discounts: true, edit_clients: true, manage_inventory: true },
  admin: { view_dashboard: true, view_reports: true, give_discounts: true, edit_clients: true, manage_inventory: true },
  reception: { view_dashboard: false, view_reports: false, give_discounts: false, edit_clients: true, manage_inventory: false },
  staff: { view_dashboard: true, view_reports: false, give_discounts: false, edit_clients: false, manage_inventory: false },
};
const ROLE_BLURB = {
  owner: "Full access to everything, including billing and staff.",
  admin: "Full access to run the clinic. Cannot be removed if they are the owner.",
  reception: "Front-desk: calendar, clients, checkout and waitlist. No dashboard, pricing or reports by default.",
  staff: "Practitioner: their own calendar, checkout and price list. Never sees cost or margin figures.",
};

const toCostInput = (f) => ({
  employmentType: f.employment_type === "casual" ? "casual" : "permanent",
  baseHourlyRate: f.base_hourly_rate, hoursPerWeek: f.hours_per_week, superPct: f.super_pct,
  workersCompPct: f.workers_comp_pct, leaveLoadingPct: f.leave_loading_pct, annualLeaveWeeks: f.annual_leave_weeks,
  personalLeaveDays: f.personal_leave_days, publicHolidayDays: f.public_holiday_days, casualLoadingPct: f.casual_loading_pct,
});

export default function Staff() {
  const { business, isAdmin } = useAuth();
  const cur = business?.currency || "AUD";
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(null); // null = list view; object = file view
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("roster");
  const [confirmDel, setConfirmDel] = useState(null); // staff pending deletion (mobile-safe dialog)
  const [deleting, setDeleting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => setForm((f) => ({ ...f, [k]: parseFloat(v) || 0 }));

  const load = () => api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty }); setEditId(null); setTab("access"); };
  const openFile = (s) => { setForm({ ...empty, ...s, permissions: s.permissions || {}, kpi_multiplier: s.kpi_multiplier ?? "", password: "" }); setEditId(s.id); setTab("roster"); };
  const closeFile = () => { setForm(null); setEditId(null); };

  const wh = form?.working_hours || DEFAULT_WH;
  const setWHDay = (day, key, val) => setForm((f) => { const base = f.working_hours || DEFAULT_WH; return { ...f, working_hours: { ...base, [day]: { ...base[day], [key]: val } } }; });
  const cost = form ? employmentCost(toCostInput(form)) : null;
  const weeklyCost = form ? (cost.mode === "casual" ? cost.trueHourly * (parseFloat(form.hours_per_week) || 0) : (cost.totalAnnual || 0) / 52) : 0;
  const kpiMult = parseFloat(form?.kpi_multiplier) || 4;
  const kpiTarget = weeklyCost * kpiMult;

  const applyCountry = (c) => {
    const p = COUNTRY_PRESETS[c];
    if (!p || p.superPct === undefined) return;
    setForm((f) => ({ ...f, super_pct: p.superPct, workers_comp_pct: p.workersCompPct, leave_loading_pct: p.leaveLoadingPct,
      annual_leave_weeks: p.annualLeaveWeeks, personal_leave_days: p.personalLeaveDays, public_holiday_days: p.publicHolidayDays,
      casual_loading_pct: p.casualLoadingPct }));
    toast.success(`Applied ${p.label} defaults`);
  };

  const permVal = (key) => {
    if (form.role === "owner" || form.role === "admin") return true;
    const ov = form.permissions || {};
    if (key in ov) return !!ov[key];
    return !!(ROLE_DEFAULTS[form.role] || {})[key];
  };
  const setPerm = (key, v) => setForm((f) => ({ ...f, permissions: { ...(f.permissions || {}), [key]: v } }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name & email required"); return; }
    if (!editId && !form.password) { toast.error("Set a password"); return; }
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.working_hours) payload.working_hours = DEFAULT_WH;
      payload.kpi_multiplier = (payload.kpi_multiplier === "" || payload.kpi_multiplier == null) ? null : parseFloat(payload.kpi_multiplier);
      if (editId) await api.put(`/staff/${editId}`, payload);
      else await api.post("/staff", payload);
      toast.success("Staff saved"); closeFile(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const remove = (s) => setConfirmDel(s);
  const doRemove = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/staff/${confirmDel.id}`);
      if (editId === confirmDel.id) closeFile();
      toast.success("Removed");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove staff");
    } finally { setDeleting(false); }
  };

  // ---------- LIST VIEW ----------
  const deleteDialog = (
    <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o && !deleting) setConfirmDel(null); }}>
      <AlertDialogContent data-testid="staff-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {confirmDel?.name}?</AlertDialogTitle>
          <AlertDialogDescription>This permanently removes their staff login and profile. This can't be undone. Their past bookings and sales stay on record.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="staff-delete-cancel">Keep staff</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); doRemove(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="staff-delete-confirm">{deleting ? "Removing…" : "Remove staff"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  if (!form) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {deleteDialog}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Staff</h1>
            <p className="text-muted-foreground mt-1">Open a staff file for their roster, access level & pay</p>
          </div>
          {isAdmin && <Button className="rounded-lg" onClick={openNew} data-testid="add-staff-button"><Plus className="h-4 w-4 mr-1.5" /> Add staff</Button>}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {staff.map((s) => (
            <Card key={s.id} className="rounded-2xl border bg-card p-4 shadow-sm cursor-pointer hover:border-primary/50 transition-colors" onClick={() => openFile(s)} data-testid={`staff-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: s.color }}>
                    {s.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </span>
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.email}</div>
                  </div>
                </div>
                {isAdmin && s.role !== "owner" && <button onClick={(e) => { e.stopPropagation(); remove(s); }} className="text-muted-foreground hover:text-destructive" data-testid={`staff-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-muted capitalize">{s.role}</span>
                <span className="px-2 py-0.5 rounded-full bg-muted">{fmtCurrency(s.base_hourly_rate, cur, 0)}/hr</span>
                {s.can_be_booked && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary">Bookable</span>}
                {s.profit_share_enabled && <span className="px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">{s.profit_share_pct}% share</span>}
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ---------- FILE VIEW ----------
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="staff-file">
      {deleteDialog}
      <button onClick={closeFile} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="staff-file-back"><ArrowLeft className="h-4 w-4" />All staff</button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-full flex items-center justify-center text-base font-bold text-white shrink-0" style={{ backgroundColor: form.color }}>
            {(form.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2) || "?"}
          </span>
          <div>
            <h1 className="font-heading font-extrabold text-2xl tracking-tight">{editId ? form.name : "New staff member"}</h1>
            <p className="text-muted-foreground text-sm capitalize">{form.role}{form.can_be_booked ? " · bookable" : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {editId && form.role !== "owner" && <Button variant="ghost" className="rounded-lg text-destructive" onClick={() => remove({ id: editId, name: form.name })} data-testid="staff-file-delete"><Trash2 className="h-4 w-4 mr-1.5" />Remove</Button>}
          <Button className="rounded-lg" onClick={save} data-testid="staff-save"><Save className="h-4 w-4 mr-1.5" />Save</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full" data-testid="staff-file-tabs">
          <TabsTrigger value="roster" data-testid="staff-tab-roster"><CalendarClock className="h-4 w-4 mr-1.5" />Roster</TabsTrigger>
          <TabsTrigger value="access" data-testid="staff-tab-access"><ShieldCheck className="h-4 w-4 mr-1.5" />Access level</TabsTrigger>
          <TabsTrigger value="pay" data-testid="staff-tab-pay"><Wallet className="h-4 w-4 mr-1.5" />Pay</TabsTrigger>
        </TabsList>

        {/* ROSTER */}
        <TabsContent value="roster" className="mt-4 space-y-3">
          <Card className="rounded-2xl border p-4" data-testid="staff-roster">
            <div className="text-sm font-medium">Weekly roster</div>
            <p className="text-xs text-muted-foreground mb-3">Start, finish and an optional break for each day. Breaks show on the diary and block online bookings.</p>
            <div className="space-y-2">
              {DAYS.map(([d, lbl]) => {
                const day = wh[d] || DEFAULT_WH[d];
                return (
                  <div key={d} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-1.5 border-b border-border/50 last:border-0" data-testid={`wh-row-${d}`}>
                    <span className="w-20 text-sm font-medium">{lbl}</span>
                    <Switch checked={!!day.open} onCheckedChange={(v) => setWHDay(d, "open", v)} data-testid={`wh-open-${d}`} />
                    {day.open ? (
                      <>
                        <Input type="time" value={day.start} onChange={(e) => setWHDay(d, "start", e.target.value)} className="h-9 rounded-lg w-28" data-testid={`wh-start-${d}`} />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input type="time" value={day.end} onChange={(e) => setWHDay(d, "end", e.target.value)} className="h-9 rounded-lg w-28" data-testid={`wh-end-${d}`} />
                        <span className="flex items-center gap-1.5 ml-auto sm:ml-2">
                          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Break</span>
                          <Input type="time" value={day.break_start || ""} onChange={(e) => setWHDay(d, "break_start", e.target.value)} className="h-9 rounded-lg w-28" data-testid={`wh-break-start-${d}`} />
                          <span className="text-xs text-muted-foreground">–</span>
                          <Input type="time" value={day.break_end || ""} onChange={(e) => setWHDay(d, "break_end", e.target.value)} className="h-9 rounded-lg w-28" data-testid={`wh-break-end-${d}`} />
                          {(day.break_start || day.break_end) && <button type="button" onClick={() => { setWHDay(d, "break_start", ""); setWHDay(d, "break_end", ""); }} className="text-xs text-muted-foreground hover:text-destructive" data-testid={`wh-break-clear-${d}`}>clear</button>}
                        </span>
                      </>
                    ) : <span className="text-xs text-muted-foreground">Day off</span>}
                  </div>
                );
              })}
            </div>
          </Card>
          <Card className="rounded-2xl border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Clinic closed dates & public holidays</div>
                <p className="text-xs text-muted-foreground">Leave / closed days apply clinic-wide.</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={() => (window.location.href = "/business-info")} data-testid="staff-closed-dates-link">Manage in Business Info</Button>
            </div>
          </Card>
        </TabsContent>

        {/* ACCESS LEVEL */}
        <TabsContent value="access" className="mt-4 space-y-3">
          <Card className="rounded-2xl border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Name</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="staff-name" /></div>
              <div><label className="text-xs text-muted-foreground">Email (login)</label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-lg" data-testid="staff-email" /></div>
              <div><label className="text-xs text-muted-foreground">Mobile (for password reset codes)</label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+61 4XX XXX XXX" className="h-11 rounded-lg" data-testid="staff-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Access level</label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{form.role === "owner" && <SelectItem value="owner">Owner</SelectItem>}<SelectItem value="admin">Admin</SelectItem><SelectItem value="reception">Reception</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-muted-foreground">{editId ? "Reset password" : "Password"}</label><Input type="password" placeholder={editId ? "Leave blank to keep" : "Set a password"} value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 rounded-lg" data-testid="staff-password" /></div>
            </div>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="staff-role-blurb">{ROLE_BLURB[form.role]}</div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Colour</span>
              {COLORS.map((c) => <button key={c} onClick={() => set("color", c)} className={`h-6 w-6 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-offset-background ring-foreground" : ""}`} style={{ backgroundColor: c }} data-testid={`staff-color-${c}`} />)}
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div><span className="text-sm font-medium">Bookable</span><div className="text-xs text-muted-foreground">Show as a bookable practitioner on the calendar & online</div></div>
              <Switch checked={form.can_be_booked} onCheckedChange={(v) => set("can_be_booked", v)} data-testid="staff-bookable" />
            </div>
          </Card>

          <Card className="rounded-2xl border p-4" data-testid="staff-permissions">
            <div className="text-sm font-medium">Permissions</div>
            <p className="text-xs text-muted-foreground mb-3">Fine-tune what this person can do, on top of their access level.{(form.role === "owner" || form.role === "admin") ? " Admins & owners always have full access." : ""}</p>
            <div className="space-y-1.5">
              {PERMS.map(([key, label, desc]) => (
                <div key={key} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`perm-row-${key}`}>
                  <div className="pr-3"><div className="text-sm font-medium">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
                  <Switch checked={permVal(key)} disabled={form.role === "owner" || form.role === "admin"} onCheckedChange={(v) => setPerm(key, v)} data-testid={`perm-${key}`} />
                </div>
              ))}
            </div>
          </Card>
          <StaffPinCard staffId={editId} />
        </TabsContent>

        {/* PAY */}
        <TabsContent value="pay" className="mt-4 space-y-3">
          <Card className="rounded-2xl border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Base wage / hr</label><Input type="number" min="0" value={form.base_hourly_rate} onChange={(e) => setNum("base_hourly_rate", e.target.value)} className="h-11 rounded-lg" data-testid="staff-wage" /></div>
              <div className="flex items-end justify-end">
                <Select onValueChange={applyCountry}>
                  <SelectTrigger className="h-11 rounded-lg w-40 text-xs" data-testid="staff-country-preset"><SelectValue placeholder="Country preset" /></SelectTrigger>
                  <SelectContent><SelectItem value="AU">Australia</SelectItem><SelectItem value="NZ">New Zealand</SelectItem><SelectItem value="USA">United States</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Employment</label>
                <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="staff-employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full_time">Full time</SelectItem><SelectItem value="part_time">Part time</SelectItem><SelectItem value="casual">Casual</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-muted-foreground">Hours / week</label><Input type="number" min="0" value={form.hours_per_week} onChange={(e) => setNum("hours_per_week", e.target.value)} className="h-10 rounded-lg" data-testid="staff-hours" /></div>
            </div>
            {form.employment_type === "casual" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Casual loading %</label><Input type="number" min="0" value={form.casual_loading_pct} onChange={(e) => setNum("casual_loading_pct", e.target.value)} className="h-10 rounded-lg" data-testid="staff-casual-loading" /></div>
                <div><label className="text-xs text-muted-foreground">Super %</label><Input type="number" min="0" value={form.super_pct} onChange={(e) => setNum("super_pct", e.target.value)} className="h-10 rounded-lg" data-testid="staff-super" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs text-muted-foreground">Super %</label><Input type="number" min="0" value={form.super_pct} onChange={(e) => setNum("super_pct", e.target.value)} className="h-10 rounded-lg" data-testid="staff-super" /></div>
                <div><label className="text-xs text-muted-foreground">Annual leave wks</label><Input type="number" min="0" value={form.annual_leave_weeks} onChange={(e) => setNum("annual_leave_weeks", e.target.value)} className="h-10 rounded-lg" data-testid="staff-annual-leave" /></div>
                <div><label className="text-xs text-muted-foreground">Leave loading %</label><Input type="number" min="0" value={form.leave_loading_pct} onChange={(e) => setNum("leave_loading_pct", e.target.value)} className="h-10 rounded-lg" data-testid="staff-leave-loading" /></div>
                <div><label className="text-xs text-muted-foreground">Sick/personal days</label><Input type="number" min="0" value={form.personal_leave_days} onChange={(e) => setNum("personal_leave_days", e.target.value)} className="h-10 rounded-lg" data-testid="staff-personal-leave" /></div>
                <div><label className="text-xs text-muted-foreground">Public hol. days</label><Input type="number" min="0" value={form.public_holiday_days} onChange={(e) => setNum("public_holiday_days", e.target.value)} className="h-10 rounded-lg" data-testid="staff-public-hol" /></div>
                <div><label className="text-xs text-muted-foreground">Workers comp %</label><Input type="number" min="0" value={form.workers_comp_pct} onChange={(e) => setNum("workers_comp_pct", e.target.value)} className="h-10 rounded-lg" data-testid="staff-workers-comp" /></div>
              </div>
            )}
            <div className="rounded-lg bg-primary/8 px-3 py-2 flex items-center justify-between" data-testid="staff-cost-breakdown">
              <div className="text-xs text-muted-foreground">Cost approx / hour worked<div className="text-[10px]">+{cost.upliftPct.toFixed(0)}% on base</div></div>
              <span className="font-heading font-bold text-lg text-primary tabular-nums" data-testid="staff-true-hourly">{fmtCurrency(cost.trueHourly, cur, 2)}</span>
            </div>
          </Card>

          <Card className="rounded-2xl border p-4">
            <div className="flex items-center justify-between gap-3">
              <div><span className="text-sm font-medium">KPI target multiplier</span><div className="text-xs text-muted-foreground">×weekly cost. Blank = business default (4).</div></div>
              <Input type="number" min="0" step="0.5" placeholder="4" value={form.kpi_multiplier} onChange={(e) => set("kpi_multiplier", e.target.value)} className="h-10 rounded-lg w-24" data-testid="staff-kpi-multiplier" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-primary/8 px-3 py-2 mt-3">
              <span className="text-xs text-muted-foreground">Weekly KPI target ({fmtCurrency(weeklyCost, cur, 0)} × {kpiMult})</span>
              <span className="font-heading font-bold text-primary tabular-nums" data-testid="staff-kpi-target">{fmtCurrency(kpiTarget, cur, 0)}/wk</span>
            </div>
          </Card>

          <Card className="rounded-2xl border p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Profit share</span>
              <Switch checked={form.profit_share_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, profit_share_enabled: v, profit_share_pct: v && !f.profit_share_pct ? 20 : f.profit_share_pct, profit_share_threshold: v && !f.profit_share_threshold ? 5000 : f.profit_share_threshold }))} data-testid="staff-profitshare-toggle" />
            </div>
            {form.profit_share_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Share %</label><Input type="number" min="0" value={form.profit_share_pct} onChange={(e) => set("profit_share_pct", parseFloat(e.target.value) || 0)} className="h-10 rounded-lg" data-testid="staff-profitshare-pct" /></div>
                <div><label className="text-xs text-muted-foreground">After revenue of</label><Input type="number" min="0" value={form.profit_share_threshold} onChange={(e) => set("profit_share_threshold", parseFloat(e.target.value) || 0)} className="h-10 rounded-lg" data-testid="staff-profitshare-threshold" /></div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
